import { useState, useEffect } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { Button } from "./ui/button";
import { Menu, X, LogOut, LayoutDashboard, User, Shield } from "lucide-react";
import axios from "axios";
import { API } from "../App";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "./ui/dropdown-menu";

// Admin emails list - should match backend
const ADMIN_EMAILS = ["3cubein@gmail.com"];

const Navbar = ({ user }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const [isScrolled, setIsScrolled] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [currentUser, setCurrentUser] = useState(user);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  useEffect(() => {
    // If no user prop, try to fetch current user
    if (!user) {
      const token = localStorage.getItem("auth_token");
      if (token) {
        axios.get(`${API}/auth/me`, {
          headers: { Authorization: `Bearer ${token}` },
          withCredentials: true
        })
        .then(res => setCurrentUser(res.data))
        .catch(() => setCurrentUser(null));
      }
    } else {
      setCurrentUser(user);
    }
  }, [user]);

  const handleLogout = async () => {
    try {
      await axios.post(`${API}/auth/logout`, {}, { withCredentials: true });
    } catch (error) {
      // Ignore errors
    }
    localStorage.removeItem("auth_token");
    navigate("/");
  };

  const isLandingPage = location.pathname === "/" || location.pathname === "/pricing";

  return (
    <nav
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        isScrolled ? "bg-background/90 backdrop-blur-lg border-b border-border" : ""
      }`}
    >
      <div className="max-w-7xl mx-auto px-6">
        <div className="flex items-center justify-between h-20">
          {/* Logo */}
          <Link to="/" className="flex items-center gap-2">
            <div className="w-8 h-8 rounded bg-primary-main flex items-center justify-center">
              <span className="font-display font-bold text-black text-lg">V</span>
            </div>
            <span className="font-display text-xl font-bold tracking-tight">
              <span className="text-white">Vendor</span>
              <span className="text-primary-main">Compare</span>
            </span>
          </Link>

          {/* Desktop Navigation */}
          <div className="hidden md:flex items-center gap-8">
            {isLandingPage && (
              <>
                <a href="#features" className="text-gray-400 hover:text-white transition-colors">
                  Features
                </a>
                <a href="#demo" className="text-gray-400 hover:text-white transition-colors">
                  How It Works
                </a>
                <a href="#pricing" className="text-gray-400 hover:text-white transition-colors">
                  Pricing
                </a>
              </>
            )}
            
            {currentUser ? (
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button 
                    variant="ghost" 
                    className="flex items-center gap-2 hover:bg-background-subtle"
                    data-testid="user-menu-btn"
                  >
                    {currentUser.picture ? (
                      <img 
                        src={currentUser.picture} 
                        alt={currentUser.name}
                        className="w-8 h-8 rounded-full"
                      />
                    ) : (
                      <div className="w-8 h-8 rounded-full bg-primary-main/20 flex items-center justify-center">
                        <User className="w-4 h-4 text-primary-main" />
                      </div>
                    )}
                    <span className="text-sm">{currentUser.name?.split(' ')[0]}</span>
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="bg-background-paper border-border w-48">
                  <div className="px-3 py-2 border-b border-border">
                    <p className="text-sm font-medium">{currentUser.name}</p>
                    <p className="text-xs text-gray-500">{currentUser.email}</p>
                    <span className="inline-block mt-1 px-2 py-0.5 text-xs font-mono uppercase bg-primary-main/10 text-primary-main rounded">
                      {currentUser.tier}
                    </span>
                  </div>
                  <DropdownMenuItem 
                    onClick={() => navigate("/dashboard")}
                    className="cursor-pointer hover:bg-background-subtle"
                  >
                    <LayoutDashboard className="w-4 h-4 mr-2" />
                    Dashboard
                  </DropdownMenuItem>
                  {ADMIN_EMAILS.includes(currentUser.email) && (
                    <DropdownMenuItem 
                      onClick={() => navigate("/admin")}
                      className="cursor-pointer hover:bg-secondary-main/10 text-secondary-main"
                      data-testid="admin-link"
                    >
                      <Shield className="w-4 h-4 mr-2" />
                      Admin Panel
                    </DropdownMenuItem>
                  )}
                  <DropdownMenuSeparator className="bg-border" />
                  <DropdownMenuItem 
                    onClick={handleLogout}
                    className="cursor-pointer text-red-400 hover:bg-red-500/10 hover:text-red-400"
                    data-testid="logout-btn"
                  >
                    <LogOut className="w-4 h-4 mr-2" />
                    Sign Out
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            ) : (
              <div className="flex items-center gap-4">
                <Link to="/login">
                  <Button variant="ghost" className="text-gray-400 hover:text-white" data-testid="nav-login-btn">
                    Sign In
                  </Button>
                </Link>
                <Link to="/signup">
                  <Button 
                    className="btn-skew bg-primary-main text-black font-bold uppercase tracking-wider px-5 hover:bg-primary-light transition-all"
                    data-testid="nav-signup-btn"
                  >
                    <span>Get Started</span>
                  </Button>
                </Link>
              </div>
            )}
          </div>

          {/* Mobile Menu Button */}
          <Button
            variant="ghost"
            size="icon"
            className="md:hidden"
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
          >
            {mobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </Button>
        </div>
      </div>

      {/* Mobile Menu */}
      {mobileMenuOpen && (
        <div className="md:hidden bg-background/95 backdrop-blur-lg border-b border-border">
          <div className="px-6 py-4 space-y-4">
            {isLandingPage && (
              <>
                <a 
                  href="#features" 
                  className="block text-gray-400 hover:text-white transition-colors"
                  onClick={() => setMobileMenuOpen(false)}
                >
                  Features
                </a>
                <a 
                  href="#demo" 
                  className="block text-gray-400 hover:text-white transition-colors"
                  onClick={() => setMobileMenuOpen(false)}
                >
                  How It Works
                </a>
                <Link 
                  to="/pricing" 
                  className="block text-gray-400 hover:text-white transition-colors"
                  onClick={() => setMobileMenuOpen(false)}
                >
                  Pricing
                </Link>
              </>
            )}
            
            {currentUser ? (
              <>
                <div className="pt-4 border-t border-border">
                  <p className="text-sm text-gray-400">{currentUser.email}</p>
                </div>
                <Link 
                  to="/dashboard"
                  className="block text-gray-400 hover:text-white transition-colors"
                  onClick={() => setMobileMenuOpen(false)}
                >
                  Dashboard
                </Link>
                {ADMIN_EMAILS.includes(currentUser.email) && (
                  <Link 
                    to="/admin"
                    className="block text-secondary-main hover:text-secondary-light transition-colors"
                    onClick={() => setMobileMenuOpen(false)}
                  >
                    Admin Panel
                  </Link>
                )}
                <button
                  onClick={() => {
                    handleLogout();
                    setMobileMenuOpen(false);
                  }}
                  className="block text-red-400 hover:text-red-300 transition-colors"
                >
                  Sign Out
                </button>
              </>
            ) : (
              <div className="flex flex-col gap-3 pt-4 border-t border-border">
                <Link to="/login" onClick={() => setMobileMenuOpen(false)}>
                  <Button variant="outline" className="w-full border-border">
                    Sign In
                  </Button>
                </Link>
                <Link to="/signup" onClick={() => setMobileMenuOpen(false)}>
                  <Button className="w-full bg-primary-main text-black">
                    Get Started
                  </Button>
                </Link>
              </div>
            )}
          </div>
        </div>
      )}
    </nav>
  );
};

export default Navbar;
